import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, DatePicker, message } from 'antd';
import { FileTextOutlined, CloseCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import TdsFileUpload from './TdsFileUpload';
import { updateVendorBillGstInvoiceImage } from 'store/vendorBillSliceV2';

const DATE_FORMAT = 'DD-MM-YYYY';
const fileNameFromKey = (key = '') => {
	const match = String(key).match(
		/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i
	);
	return match ? key.slice(match[0].length) : String(key).split('/').pop();
};

function GstInvoiceUploadModal({
	visible,
	onClose,
	vendorBillId,
	useV2API = false,
	onSuccess,
	isGstRegistered = false,
	initialValues,
}) {
	const dispatch = useDispatch();
	const [files, setFiles] = useState([]);
	const [invoiceNumber, setInvoiceNumber] = useState('');
	const [invoiceDate, setInvoiceDate] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [removedExisting, setRemovedExisting] = useState(false);
	const existingImageKey = initialValues?.image_key || null;
	const showExisting = Boolean(existingImageKey) && !removedExisting && files.length === 0;

	useEffect(() => {
		if (visible) {
			setInvoiceNumber(initialValues?.invoice_number || '');
			setInvoiceDate(initialValues?.invoice_date ? moment(initialValues.invoice_date) : null);
			setFiles([]);
			setRemovedExisting(false);
		}
	}, [visible, initialValues]);

	const handleUpload = async () => {
		if (!vendorBillId) {
			message.error('Vendor bill ID is required');
			return;
		}

		const newKey = files[0]?.filename || files[0]?.key || files[0]?.url || null;
		const s3Key = newKey || (removedExisting ? null : existingImageKey);

		if (!newKey && !existingImageKey && !removedExisting) {
			message.warning('Please select a file to upload');
			return;
		}

		setUploading(true);

		try {
			const response = await dispatch(
				updateVendorBillGstInvoiceImage({
					vendor_bill_id: Number(vendorBillId),
					isGstRegistered,
					image_key: s3Key,
					// Invoice number/date captured for both GST and non-GST vendors.
					invoice_number: invoiceNumber ? invoiceNumber.trim() : null,
					invoice_date: invoiceDate ? invoiceDate.format('YYYY-MM-DD') : null,
					useV2API,
				})
			);

			if (response?.status === 'success') {
				message.success('Invoice saved successfully');
				setFiles([]);
				if (onSuccess) {
					onSuccess();
				}
				onClose();
			} else {
				const errorMessage =
					response?.error?.message || response?.message || 'Failed to save invoice';
				message.error(errorMessage);
			}
		} catch (error) {
			console.error('Invoice save error:', error);
			const errorMessage =
				error?.response?.data?.error?.message || error?.message || 'Failed to save invoice';
			message.error(errorMessage);
		} finally {
			setUploading(false);
		}
	};

	const handleCancel = () => {
		if (!uploading) {
			setFiles([]);
			onClose();
		}
	};

	const canSubmit = files.length > 0 || Boolean(existingImageKey) || removedExisting;

	return (
		<Modal
			title="Attach GST Invoice"
			visible={visible}
			onCancel={handleCancel}
			footer={[
				<Button key="cancel" onClick={handleCancel} disabled={uploading}>
					Cancel
				</Button>,
				<Button
					key="upload"
					type="primary"
					onClick={handleUpload}
					loading={uploading}
					disabled={!canSubmit}
				>
					Save
				</Button>,
			]}
			width={600}
			centered
		>
			{/* Invoice number/date captured for both GST and non-GST vendors. */}
			<div style={{ marginBottom: 16 }}>
				<label
					htmlFor="gst-modal-invoice-number"
					style={{ display: 'block', marginBottom: 4 }}
				>
					Invoice Number
				</label>
				<Input
					id="gst-modal-invoice-number"
					placeholder="Enter invoice number"
					value={invoiceNumber}
					maxLength={100}
					onChange={(e) => setInvoiceNumber(e.target.value)}
				/>
			</div>

			<div style={{ marginBottom: 16 }}>
				<label
					htmlFor="gst-modal-invoice-date"
					style={{ display: 'block', marginBottom: 4 }}
				>
					Invoice Date
				</label>
				<DatePicker
					id="gst-modal-invoice-date"
					style={{ width: '100%' }}
					format={DATE_FORMAT}
					value={invoiceDate}
					onChange={(date) => setInvoiceDate(date)}
				/>
			</div>

			<div style={{ marginBottom: 8 }}>
				<p style={{ marginBottom: 8 }}>
					Select an invoice file to upload (PDF, PNG, JPG, JPEG)
				</p>
				{showExisting && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							marginBottom: 12,
							padding: '6px 10px',
							border: '1px solid #e5e7eb',
							borderRadius: 6,
						}}
					>
						<FileTextOutlined style={{ color: '#1890ff' }} />
						<span style={{ flex: 1, fontSize: 13, wordBreak: 'break-all' }}>
							{fileNameFromKey(existingImageKey)}
						</span>
						<CloseCircleOutlined
							style={{ color: '#ff4d4f', cursor: 'pointer' }}
							title="Remove attached invoice"
							onClick={() => setRemovedExisting(true)}
						/>
					</div>
				)}
				{removedExisting && files.length === 0 && (
					<p style={{ marginBottom: 8, fontSize: 12, color: '#ff4d4f' }}>
						Attached invoice will be removed on Save. Upload a new file to replace it
						instead.
					</p>
				)}
				<TdsFileUpload
					files={files}
					onFilesChange={setFiles}
					maxFiles={1}
					entity="vendor_bill"
				/>
			</div>
		</Modal>
	);
}

GstInvoiceUploadModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	vendorBillId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	useV2API: PropTypes.bool,
	onSuccess: PropTypes.func,
	isGstRegistered: PropTypes.bool,
	initialValues: PropTypes.shape({
		invoice_number: PropTypes.string,
		invoice_date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
		image_key: PropTypes.string,
	}),
};

export default GstInvoiceUploadModal;
